import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStaffAuth } from './StaffAuthContext';
import { requestedStaffAdminPath } from './staffAdminReturnPath';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ms-root {
    min-height: 100vh;
    background: #080B12;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    padding: 40px 24px 60px;
    position: relative;
    overflow: hidden;
  }

  .ms-bg-grid {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(rgba(201,168,76,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(201,168,76,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    pointer-events: none;
  }

  .ms-wrap {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 560px;
    animation: ms-in 0.5s ease both;
  }

  @keyframes ms-in {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ms-header {
    text-align: center;
    margin-bottom: 40px;
  }

  .ms-logo-mark {
    width: 52px;
    height: 52px;
    background: linear-gradient(135deg, #C9A84C, #8B0000);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 22px;
    color: #fff;
    margin: 0 auto 20px;
  }

  .ms-title {
    font-family: 'Syne', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.5px;
    margin-bottom: 8px;
  }

  .ms-subtitle {
    font-size: 14px;
    color: rgba(255,255,255,0.4);
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
  }

  .ms-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    margin-bottom: 40px;
  }

  .ms-step-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Syne', sans-serif;
    transition: all 0.3s;
    flex-shrink: 0;
  }

  .ms-step-dot.active   { background: #C9A84C; color: #080B12; }
  .ms-step-dot.done     { background: rgba(201,168,76,0.2); color: #C9A84C; border: 1px solid rgba(201,168,76,0.4); }
  .ms-step-dot.upcoming { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.25); border: 1px solid rgba(255,255,255,0.08); }

  .ms-step-line {
    flex: 1;
    height: 1px;
    background: rgba(255,255,255,0.08);
    max-width: 60px;
  }

  .ms-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px;
    padding: 36px;
    margin-bottom: 16px;
  }

  .ms-card-title {
    font-family: 'Syne', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 6px;
  }

  .ms-card-desc {
    font-size: 13px;
    color: rgba(255,255,255,0.4);
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .ms-apps {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .ms-app {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
  }

  .ms-app-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
  }

  .ms-app-name {
    font-size: 13px;
    font-weight: 500;
    color: rgba(255,255,255,0.7);
  }

  .ms-qr-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .ms-qr-img {
    width: 200px;
    height: 200px;
    border-radius: 16px;
    border: 3px solid rgba(201,168,76,0.3);
    background: #fff;
    padding: 8px;
  }

  .ms-secret-box {
    width: 100%;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .ms-secret-label {
    font-size: 11px;
    color: rgba(255,255,255,0.3);
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .ms-secret-val {
    font-family: 'DM Mono', monospace;
    font-size: 14px;
    color: #C9A84C;
    letter-spacing: 2px;
    word-break: break-all;
  }

  .ms-copy-btn {
    background: rgba(201,168,76,0.1);
    border: 1px solid rgba(201,168,76,0.25);
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    color: #C9A84C;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s;
    flex-shrink: 0;
  }

  .ms-copy-btn:hover { background: rgba(201,168,76,0.18); }

  .ms-otp-inputs {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-bottom: 24px;
  }

  .ms-otp-input {
    width: 52px;
    height: 60px;
    text-align: center;
    font-family: 'Syne', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    background: rgba(255,255,255,0.05);
    border: 2px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    caret-color: #C9A84C;
  }

  .ms-otp-input:focus {
    border-color: #C9A84C;
    background: rgba(201,168,76,0.06);
  }

  .ms-otp-input.error { border-color: #EF4444; }

  .ms-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #C9A84C, #A0832E);
    border: none;
    border-radius: 10px;
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #080B12;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.1s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .ms-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .ms-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .ms-btn-ghost {
    width: 100%;
    padding: 13px;
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: rgba(255,255,255,0.5);
    cursor: pointer;
    margin-top: 10px;
    transition: border-color 0.2s, color 0.2s;
  }
  .ms-btn-ghost:hover { border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.7); }

  .ms-error {
    padding: 12px 16px;
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.25);
    border-radius: 10px;
    font-size: 13px;
    color: #FCA5A5;
    margin-bottom: 16px;
  }

  .ms-recovery-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 20px;
  }

  .ms-recovery-code {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    color: #C9A84C;
    background: rgba(201,168,76,0.06);
    border: 1px solid rgba(201,168,76,0.15);
    border-radius: 8px;
    padding: 10px 14px;
    letter-spacing: 1px;
    text-align: center;
  }

  .ms-checkbox-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
    margin-bottom: 20px;
    cursor: pointer;
  }

  .ms-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255,255,255,0.2);
    border-radius: 6px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
    transition: all 0.2s;
    cursor: pointer;
  }

  .ms-checkbox.checked {
    background: #C9A84C;
    border-color: #C9A84C;
  }

  .ms-checkbox-label {
    font-size: 13px;
    color: rgba(255,255,255,0.6);
    line-height: 1.5;
  }

  .ms-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(8,11,18,0.3);
    border-top-color: #080B12;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  .ms-loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 40px 0;
  }

  .ms-loading-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid rgba(201,168,76,0.15);
    border-top-color: #C9A84C;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .ms-loading-text {
    font-size: 14px;
    color: rgba(255,255,255,0.4);
  }

  .ms-warning {
    padding: 14px 16px;
    background: rgba(245,158,11,0.08);
    border: 1px solid rgba(245,158,11,0.2);
    border-radius: 10px;
    font-size: 13px;
    color: #FCD34D;
    margin-bottom: 20px;
    line-height: 1.5;
  }

  @media (max-width: 540px) {
    .ms-card { padding: 28px 20px; }
    .ms-apps { grid-template-columns: 1fr; }
    .ms-otp-input { width: 44px; height: 54px; font-size: 20px; }
    .ms-recovery-grid { grid-template-columns: 1fr; }
  }
`;

const STEPS = ['Install App', 'Scan QR Code', 'Verify Code', 'Recovery Codes'];

const APPS = [
  { icon: 'G', name: 'Google Authenticator',   bg: '#4285F4' },
  { icon: 'M', name: 'Microsoft Authenticator', bg: '#00A4EF' },
  { icon: 'A', name: 'Authy',                   bg: '#EC1313' },
  { icon: '1', name: '1Password',                bg: '#1A8CFF' },
];

export default function StaffMFASetup() {
  const [step,          setStep]          = useState(0);
  const [setupData,     setSetupData]     = useState(null);
  const [loadingSetup,  setLoadingSetup]  = useState(false);
  const [otp,           setOtp]           = useState(['','','','','','']);
  const [verifying,     setVerifying]     = useState(false);
  const [error,         setError]         = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [confirmed,     setConfirmed]     = useState(false);
  const [copied,        setCopied]        = useState(false);
  const inputRefs = useRef([]);
  const { getMFASetup, verifyMFASetup } = useStaffAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = requestedStaffAdminPath(location);

  useEffect(() => {
    if (step === 1 && !setupData) {
      setLoadingSetup(true);
      getMFASetup()
        .then(data => setSetupData(data))
        .catch(() => setError('Failed to load MFA setup. Please restart login.'))
        .finally(() => setLoadingSetup(false));
    }
  }, [step]);

  useEffect(() => {
    if (step === 2) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  const handleOtpChange = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    setError('');
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      setError('');
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) { setError('Please enter all 6 digits.'); return; }
    setVerifying(true);
    setError('');
    try {
      const data = await verifyMFASetup(code);
      setRecoveryCodes(data.recovery_codes || []);
      setStep(3);
    } catch (err) {
      const msg = err?.data?.error || '';
      setError(msg.includes('Invalid') ? 'Incorrect code. Please try again with a fresh code.' : 'Verification failed. Please try again.');
      setOtp(['','','','','','']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setVerifying(false);
    }
  };

  const handleCopySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFinish = () => {
    navigate(returnTo, { replace: true });
  };

  const stepStatus = (i) => {
    if (i < step) return 'done';
    if (i === step) return 'active';
    return 'upcoming';
  };

  return (
    <>
      <style>{styles}</style>
      <div className="ms-root">
        <div className="ms-bg-grid" />
        <div className="ms-wrap">

          <div className="ms-header">
            <div className="ms-logo-mark">VI</div>
            <h1 className="ms-title">Secure Your Staff Account</h1>
            <p className="ms-subtitle">VISINVOKE requires multi-factor authentication for all staff accounts. This protects the platform, customer data, and operational controls.</p>
          </div>

          <div className="ms-steps">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="ms-step-line" />}
                <div className={`ms-step-dot ${stepStatus(i)}`}>
                  {i < step ? '✓' : i + 1}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* STEP 0: Install App */}
          {step === 0 && (
            <div className="ms-card" style={{ animation: 'ms-in 0.4s ease both' }}>
              <div className="ms-card-title">Install an Authenticator App</div>
              <div className="ms-card-desc">Download one of these apps on your phone. Any standard TOTP authenticator will work.</div>
              <div className="ms-apps">
                {APPS.map(app => (
                  <div key={app.name} className="ms-app">
                    <div className="ms-app-icon" style={{ background: app.bg, color: '#fff', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{app.icon}</div>
                    <div className="ms-app-name">{app.name}</div>
                  </div>
                ))}
              </div>
              <button className="ms-btn" style={{ marginTop: 28 }} onClick={() => setStep(1)}>
                I have an authenticator app
              </button>
            </div>
          )}

          {/* STEP 1: QR Code */}
          {step === 1 && (
            <div className="ms-card" style={{ animation: 'ms-in 0.4s ease both' }}>
              <div className="ms-card-title">Scan the QR Code</div>
              <div className="ms-card-desc">Open your authenticator app and scan this QR code to add your VISINVOKE staff account.</div>

              {loadingSetup ? (
                <div className="ms-loading-state">
                  <div className="ms-loading-spinner" />
                  <div className="ms-loading-text">Generating secure setup...</div>
                </div>
              ) : setupData ? (
                <div className="ms-qr-wrap">
                  <img
                    className="ms-qr-img"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.totp_uri)}`}
                    alt="MFA QR Code"
                  />
                  <div className="ms-secret-box">
                    <div>
                      <div className="ms-secret-label">Manual Entry Key</div>
                      <div className="ms-secret-val">{setupData.secret}</div>
                    </div>
                    <button className="ms-copy-btn" onClick={handleCopySecret}>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              ) : (
                error && <div className="ms-error">{error}</div>
              )}

              <button className="ms-btn" style={{ marginTop: 28 }} onClick={() => setStep(2)} disabled={!setupData}>
                QR code scanned, continue
              </button>
              <button className="ms-btn-ghost" onClick={() => setStep(0)}>Back</button>
            </div>
          )}

          {/* STEP 2: Verify Code */}
          {step === 2 && (
            <div className="ms-card" style={{ animation: 'ms-in 0.4s ease both' }}>
              <div className="ms-card-title">Verify Your Authenticator</div>
              <div className="ms-card-desc">Enter the 6-digit code shown in your authenticator app to confirm the setup.</div>

              {error && <div className="ms-error">{error}</div>}

              <div className="ms-otp-inputs" onPaste={handleOtpPaste}>
                {otp.map((val, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    className={`ms-otp-input${error ? ' error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={val}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                  />
                ))}
              </div>

              <button className="ms-btn" onClick={handleVerify} disabled={verifying || otp.join('').length !== 6}>
                {verifying ? <><div className="ms-spinner" /> Verifying...</> : 'Confirm Code'}
              </button>
              <button className="ms-btn-ghost" onClick={() => setStep(1)}>Back</button>
            </div>
          )}

          {/* STEP 3: Recovery Codes */}
          {step === 3 && (
            <div className="ms-card" style={{ animation: 'ms-in 0.4s ease both' }}>
              <div className="ms-card-title" style={{ color: '#4ADE80' }}>MFA Activated</div>
              <div className="ms-card-desc">Save these recovery codes in a secure location. Each code can only be used once. These will not be shown again.</div>

              <div className="ms-warning">
                Store these in a password manager or printed document. If you lose access to your authenticator and have no recovery codes, your account will require an admin reset.
              </div>

              <div className="ms-recovery-grid">
                {recoveryCodes.map((code, i) => (
                  <div key={i} className="ms-recovery-code">{code}</div>
                ))}
              </div>

              <div className="ms-checkbox-row" onClick={() => setConfirmed(!confirmed)}>
                <div className={`ms-checkbox${confirmed ? ' checked' : ''}`}>
                  {confirmed && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#080B12" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                <div className="ms-checkbox-label">I have safely stored these recovery codes somewhere secure.</div>
              </div>

              <button className="ms-btn" onClick={handleFinish} disabled={!confirmed}>
                Continue to Admin
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
